import React from 'react';
import type { SelectedVase } from '../../../types';
import type {
  CanvasSectionBlock,
  HomepageDesignSystemSettings,
  HomepageSectionSettings,
} from '../sectionRegistry';
import {
  DEFAULT_FONT_LIBRARY,
  type DesignSystemTypography,
} from '../../../lib/typography';
import { ProductCardRows } from './ProductCardRows';
import { ProductCardActions } from './ProductCardActions';
import { resolveProductCardRenderModel } from './productCardRenderModel';

type WrapperProps = React.HTMLAttributes<HTMLDivElement> & {
  style?: React.CSSProperties;
};

type ProductCardBlockRendererProps = {
  block: CanvasSectionBlock;
  blockWrapperProps: WrapperProps;
  selectableBlockHitProps: Record<string, string>;
  renderSelectionOverlay: (
    block: CanvasSectionBlock,
    style?: React.CSSProperties,
    refCallback?: (node: HTMLDivElement | null) => void
  ) => React.ReactNode;
  logicNodeIdTag: React.ReactNode;
  productRecords: any[];
  selectedProductIds: Set<number>;
  designSystemOverride?: HomepageDesignSystemSettings;
  settings?: HomepageSectionSettings;
  w: number;
  h: number;
  x: number;
  y: number;
  columnWidthPx: number;
  rowHeightPx: number;
  scaleMode: boolean;
  isAdmin: boolean;
  isInteractPreview: boolean;
  activeProjectionRows: number;
  layerSize: { width: number; height: number };
  fontLibrary?: Record<string, string>;
  typography: DesignSystemTypography;
  useFluidSectionTypography: boolean;
  hoveredProductCardId: string | null;
  setHoveredProductCardId: React.Dispatch<React.SetStateAction<string | null>>;
  productCardRuntimeSnapshotsRef: React.MutableRefObject<Record<string, Record<string, unknown>>>;
  productCardWrapperNodesRef: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  productCardArticleNodesRef: React.MutableRefObject<Record<string, HTMLElement | null>>;
  productCardOverlayNodesRef: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  setProductCardMeasureNode: (blockId: string, node: HTMLElement | null) => void;
  flow: any;
  sectionId?: string;
  adminPreview?: { onSelectBlock?: (blockId: string | null) => void };
  onSelectVase?: (vase: SelectedVase) => void;
  setActiveModelPreview: React.Dispatch<React.SetStateAction<any>>;
};

export function ProductCardBlockRenderer({
  block,
  blockWrapperProps,
  selectableBlockHitProps,
  renderSelectionOverlay,
  logicNodeIdTag,
  productRecords,
  selectedProductIds,
  designSystemOverride,
  settings,
  w,
  h,
  x,
  y,
  columnWidthPx,
  rowHeightPx,
  scaleMode,
  isAdmin,
  isInteractPreview,
  activeProjectionRows,
  layerSize,
  fontLibrary = DEFAULT_FONT_LIBRARY,
  typography,
  useFluidSectionTypography,
  hoveredProductCardId,
  setHoveredProductCardId,
  productCardRuntimeSnapshotsRef,
  productCardWrapperNodesRef,
  productCardArticleNodesRef,
  productCardOverlayNodesRef,
  setProductCardMeasureNode,
  flow,
  sectionId,
  adminPreview,
  onSelectVase,
  setActiveModelPreview,
}: ProductCardBlockRendererProps) {
  const {
    actions,
    autoSizeResolved,
    baseCardTransform,
    buttonJustifyClass,
    buttonTypographyStyle,
    cardBoxShadow,
    hoverAnimation,
    hoverCardTransform,
    hoverContainerTransform,
    hoverGlowBoxShadow,
    hoverShadowTransform,
    isHoverActive,
    isSelectedProduct,
    naturalCardHeightPx,
    naturalCardWidthPx,
    primaryButtonVariant,
    productCardButtonsDomId,
    productCardDescriptionDomId,
    productCardDomId,
    productCardImageDomId,
    productCardImageFrameStyle,
    productCardImageObjectPosition,
    productCardImageOverlayStyle,
    productCardOverlayStyle,
    productCardShadowDomId,
    productCardWrapperDomId,
    productCardWrapperStyle,
    resolvedDescription,
    resolvedImage,
    resolvedImageAlt,
    resolvedImageSrc,
    resolvedModelPath,
    resolvedName,
    resolvedProduct,
    rowOrder,
    runtimeSnapshot,
    secondaryButtonVariant,
    selectedModelReference,
    shouldRevealButtons,
    shouldRevealDescription,
    template,
    textAlignClass,
    titleTypographyStyle,
    descriptionTypographyStyle,
  } = resolveProductCardRenderModel({
    block,
    productRecords,
    selectedProductIds,
    designSystemOverride,
    settings,
    w,
    h,
    x,
    y,
    columnWidthPx,
    rowHeightPx,
    scaleMode,
    isAdmin,
    isInteractPreview,
    activeProjectionRows,
    layerSize,
    fontLibrary,
    typography,
    useFluidSectionTypography,
    hoveredProductCardId,
  });

  productCardRuntimeSnapshotsRef.current[block.id] = runtimeSnapshot;

          return (
            <div
              key={block.id}
              data-builder-product-card-wrapper-id={productCardWrapperDomId}
              ref={(node) => {
                if (node) {
                  productCardWrapperNodesRef.current[block.id] = node;
                } else {
                  delete productCardWrapperNodesRef.current[block.id];
                }
              }}
              {...blockWrapperProps}
              style={productCardWrapperStyle}
              onPointerEnter={() => {
                if (!template.hoverEffectEnabled) return;
                setHoveredProductCardId(block.id);
              }}
              onPointerLeave={() => {
                setHoveredProductCardId((current) => (current === block.id ? null : current));
              }}
            >
              {logicNodeIdTag}
              <div
                className="absolute inset-0 overflow-visible"
                style={{
                  transform: isHoverActive ? hoverContainerTransform : 'none',
                  transition: 'transform 180ms ease',
                  transformOrigin: 'center center',
                }}
              >
                <div
                  data-builder-product-card-shadow-id={productCardShadowDomId}
                  className="pointer-events-none absolute inset-0"
                  style={{
                    borderRadius: `${template.cardRadius}px`,
                    transform:
                      isHoverActive && hoverAnimation === 'lift-shadow'
                        ? hoverShadowTransform
                        : 'none',
                    boxShadow: isHoverActive ? hoverGlowBoxShadow ?? cardBoxShadow : cardBoxShadow,
                    transition: 'transform 180ms ease, box-shadow 180ms ease',
                  }}
                />
                <div
                  className={`absolute inset-0 ${scaleMode ? 'overflow-visible' : 'overflow-hidden'}`}
                  style={{ borderRadius: scaleMode ? undefined : `${template.cardRadius}px` }}
                >
                <article
                  ref={(node) => {
                    if (node) {
                      productCardArticleNodesRef.current[block.id] = node;
                    } else {
                      delete productCardArticleNodesRef.current[block.id];
                    }
                  }}
                  data-builder-product-card-id={productCardDomId}
                  {...selectableBlockHitProps}
                  className={`absolute flex flex-col overflow-hidden border transition-[transform,box-shadow] ${
                    scaleMode ? '' : 'min-h-full'
                  }`}
                  style={{
                    left: 0,
                    top: 0,
                    width: `${naturalCardWidthPx}px`,
                    height: `${naturalCardHeightPx}px`,
                    borderRadius: `${template.cardRadius}px`,
                    padding: `${template.cardPadding}px`,
                    backgroundColor: template.cardBackgroundColor ?? '#fafaf9',
                    borderColor: template.cardBorderColor ?? '#e7e5e4',
                    borderWidth: `${template.cardBorderWidth}px`,
                    borderStyle: template.cardBorderStyle,
                    transform: isHoverActive ? hoverCardTransform : baseCardTransform,
                    transformOrigin: 'top left',
                    transition: 'transform 180ms ease',
                  }}
                >
                  <div className={`flex min-h-0 flex-1 flex-col ${textAlignClass}`}>
                    <ProductCardRows
                      rowOrder={rowOrder}
                      template={template}
                      productCardImageDomId={productCardImageDomId}
                      productCardDescriptionDomId={productCardDescriptionDomId}
                      productCardImageFrameStyle={productCardImageFrameStyle}
                      productCardImageObjectPosition={productCardImageObjectPosition}
                      productCardImageOverlayStyle={productCardImageOverlayStyle}
                      resolvedImage={resolvedImage}
                      resolvedImageSrc={resolvedImageSrc}
                      resolvedImageAlt={resolvedImageAlt}
                      resolvedName={resolvedName}
                      resolvedDescription={resolvedDescription}
                      titleTypographyStyle={titleTypographyStyle}
                      descriptionTypographyStyle={descriptionTypographyStyle}
                      isHoverActive={isHoverActive}
                      hoverAnimation={hoverAnimation}
                      shouldRevealDescription={shouldRevealDescription}
                    />
                    <ProductCardActions
                      block={block}
                      actions={actions}
                      template={template}
                      buttonJustifyClass={buttonJustifyClass}
                      buttonTypographyStyle={buttonTypographyStyle}
                      primaryButtonVariant={primaryButtonVariant}
                      secondaryButtonVariant={secondaryButtonVariant}
                      isSelectedProduct={isSelectedProduct}
                      productCardButtonsDomId={productCardButtonsDomId}
                      shouldRevealButtons={shouldRevealButtons}
                      isHoverActive={isHoverActive}
                      isAdmin={isAdmin}
                      isInteractPreview={isInteractPreview}
                      adminPreview={adminPreview}
                      resolvedProduct={resolvedProduct}
                      selectedModelReference={selectedModelReference}
                      resolvedName={resolvedName}
                      resolvedDescription={resolvedDescription}
                      resolvedImage={resolvedImage}
                      resolvedImageAlt={resolvedImageAlt}
                      resolvedModelPath={resolvedModelPath}
                      flow={flow}
                      sectionId={sectionId}
                      onSelectVase={onSelectVase}
                      setActiveModelPreview={setActiveModelPreview}
                    />
                  </div>
                </article>
                </div>
                {!autoSizeResolved && !scaleMode ? (
                  <article
                    ref={(node) => setProductCardMeasureNode(block.id, node)}
                    aria-hidden="true"
                    className="pointer-events-none invisible absolute left-0 top-0 flex h-auto flex-col overflow-visible border"
                    style={{
                      width: `${naturalCardWidthPx}px`,
                      borderRadius: `${template.cardRadius}px`,
                      padding: `${template.cardPadding}px`,
                      backgroundColor: template.cardBackgroundColor ?? '#fafaf9',
                      borderColor: template.cardBorderColor ?? '#e7e5e4',
                      borderWidth: `${template.cardBorderWidth}px`,
                      borderStyle: template.cardBorderStyle,
                      boxShadow: 'none',
                    }}
                  >
                    <div className={`flex h-auto flex-col ${textAlignClass}`}>
                      <ProductCardRows
                        measure
                        rowOrder={rowOrder}
                        template={template}
                        productCardImageFrameStyle={productCardImageFrameStyle}
                        productCardImageObjectPosition={productCardImageObjectPosition}
                        productCardImageOverlayStyle={productCardImageOverlayStyle}
                        resolvedImage={resolvedImage}
                        resolvedImageSrc={resolvedImageSrc}
                        resolvedImageAlt={resolvedImageAlt}
                        resolvedName={resolvedName}
                        resolvedDescription={resolvedDescription}
                        titleTypographyStyle={titleTypographyStyle}
                        descriptionTypographyStyle={descriptionTypographyStyle}
                        isHoverActive={isHoverActive}
                        hoverAnimation={hoverAnimation}
                        shouldRevealDescription={shouldRevealDescription}
                      />
                      <ProductCardActions
                        measure
                        block={block}
                        actions={actions}
                        template={template}
                        buttonJustifyClass={buttonJustifyClass}
                        buttonTypographyStyle={buttonTypographyStyle}
                        primaryButtonVariant={primaryButtonVariant}
                        secondaryButtonVariant={secondaryButtonVariant}
                        isSelectedProduct={isSelectedProduct}
                        shouldRevealButtons={shouldRevealButtons}
                        isHoverActive={isHoverActive}
                        isAdmin={isAdmin}
                        isInteractPreview={isInteractPreview}
                        adminPreview={adminPreview}
                        resolvedProduct={resolvedProduct}
                        selectedModelReference={selectedModelReference}
                        resolvedName={resolvedName}
                        resolvedDescription={resolvedDescription}
                        resolvedImage={resolvedImage}
                        resolvedImageAlt={resolvedImageAlt}
                        resolvedModelPath={resolvedModelPath}
                        flow={flow}
                        sectionId={sectionId}
                        onSelectVase={onSelectVase}
                        setActiveModelPreview={setActiveModelPreview}
                      />
                    </div>
                  </article>
                ) : null}
              </div>
              {renderSelectionOverlay(
                block,
                productCardOverlayStyle,
                (node) => {
                  if (node) {
                    productCardOverlayNodesRef.current[block.id] = node;
                  } else {
                    delete productCardOverlayNodesRef.current[block.id];
                  }
                }
              )}
            </div>
          );
}
